import { useState } from 'react';
import { useAtomValue, useSetAtom } from 'jotai';
import { IconButton, Stack, SxProps, Typography } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { useHavePermission } from '@/hooks';
import { usePoiAttachments } from '@/hooks/usePoiAttachments';
import {
  poiAttachmentsAtom,
  preInspectionImagesAtom,
} from '@/components/Analysis/modules/pointOfInterest/state/atoms';
import { PointOfInterest } from '@/__generated__/graphql';
import UploadAttachmentsDialog from './UploadAttachmentsDialog';
import { AttachmentsViewer } from './AttachmentsViewer';
import { Permissions, ScreenshotLocationType } from '@/types';
import { MediaActionButtons } from '@/components/shared/MediaActionButtons';
import { LocationTag } from '../../../../../../../constants';

type Props = {
  sx?: SxProps
  selectedPoiInfo: PointOfInterest;
  isUpdating: boolean;
  setIsUpdating: React.Dispatch<React.SetStateAction<boolean>>;
  handleCaptureView: (location: ScreenshotLocationType) => Promise<void>;
};

export const MediaUploadsAndViewer = ({
  sx,
  selectedPoiInfo,
  isUpdating,
  setIsUpdating,
  handleCaptureView,
}: Props) => {
  const [openAttachmentsDialog, setOpenAttachmentsDialog] = useState<boolean>(false);
  const [remediationNote, setRemediationNote] = useState<string | undefined | null>(
    selectedPoiInfo.remediationNote
  );

  usePoiAttachments({
    selectedPoiInfo,
    isUpdating,
  });

  const preInspectionImages = useAtomValue(preInspectionImagesAtom);
  const setPoiAttachments = useSetAtom(poiAttachmentsAtom);

  const isAddMode = selectedPoiInfo.files && selectedPoiInfo.files.length === 0;
  const dialogTitle = isAddMode ? 'Add Note' : 'Edit Note';
  const buttonText = isAddMode ? 'Add' : 'Update';
  const handleDialogToggle = () => setOpenAttachmentsDialog(!openAttachmentsDialog);
  const userCanUploadMedia = useHavePermission(Permissions.CREATE_UPLOAD_MEDIA);

  return (
    <>
      <Stack direction="row" alignItems="center" justifyContent="space-between" my={2}>
        <Typography variant="h6"> Note</Typography>
        {!isAddMode && userCanUploadMedia && (
          <IconButton onClick={handleDialogToggle}>
            <EditIcon />
          </IconButton>
        )}
      </Stack>
      <Stack gap="1.4rem">
        <Typography variant="caption">{remediationNote ?? ''}</Typography>
        <MediaActionButtons
          loading={isUpdating}
          onUploadClick={handleDialogToggle}
          onCaptureClick={() => handleCaptureView('attachments')}
          location={LocationTag.ViewerSelectedPoi}
        />
      </Stack>
      <AttachmentsViewer
        sx={sx}
        attachments={preInspectionImages}
        allAttachments={preInspectionImages}
        setPoiAttachments={setPoiAttachments}
        selectedPoiInfo={selectedPoiInfo}
        setIsUpdating={setIsUpdating}
        isUpdating={isUpdating}
        location={LocationTag.ViewerSelectedPoiOverview}
      />
      <UploadAttachmentsDialog
        openDialog={openAttachmentsDialog}
        setOpenDialog={setOpenAttachmentsDialog}
        pointOfInterest={selectedPoiInfo}
        attachments={[...preInspectionImages]}
        dialogTitle={dialogTitle}
        buttonText={buttonText}
        setAttachments={setPoiAttachments}
        setIsUpdating={setIsUpdating}
        setRemediationNote={setRemediationNote}
      />
    </>
  );
};
